/**
 * lib/cashFlow.ts
 * ------------------------------------------------------------------
 * THE CASH FLOW STATEMENT — where the money actually went.
 *
 * The Income & Expenditure statement answers "did we make a surplus". It cannot
 * answer "why is there no money in the bank", and for a school those come apart
 * constantly: a healthy surplus sitting entirely in unpaid fees, a term's income
 * received in advance, a vehicle bought outright. Every one of those moves cash
 * without moving the surplus, or the reverse.
 *
 * DIRECT METHOD, BUILT FROM THE CASH ACCOUNTS THEMSELVES. Rather than starting
 * from the surplus and adjusting it (the indirect method), every journal line
 * that touches a cash account is taken as fact and classified by what sat on the
 * OTHER side of that journal:
 *
 *   contra is income or expense      → OPERATING   (trading)
 *   contra is a receivable/payable   → OPERATING   (working capital)
 *   contra is a non-current asset    → INVESTING   (buying or selling assets)
 *   contra is equity or a long-term
 *     liability                      → FINANCING   (funding the school)
 *   contra is another cash account   → excluded    (a transfer, not a flow)
 *
 * That last exclusion matters more than it looks: moving P200,000 from the main
 * account to the second account is not P200,000 of anything, and a statement
 * that counts it shows a school with twice the cash movement it really had.
 *
 * THE CHECK THAT MAKES IT TRUSTWORTHY. Because every figure comes from an actual
 * cash movement, the classified totals MUST sum to the change in cash over the
 * period. `reconciles` proves it rather than asserting it — a cash flow statement
 * that does not tie to the bank movement is decoration.
 *
 * PURE. No Firestore, no React.
 * ------------------------------------------------------------------ */
import type { GlAccount, GlJournal, SystemKey } from '@/lib/gl';

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

export type FlowSection = 'operating' | 'investing' | 'financing';

export const FLOW_SECTION_LABEL: Record<FlowSection, string> = {
  operating: 'Operating activities',
  investing: 'Investing activities',
  financing: 'Financing activities',
};

export const FLOW_SECTION_HELP: Record<FlowSection, string> = {
  operating: 'Running the school — fees collected, salaries and suppliers paid.',
  investing: 'Buying and selling things the school keeps — buildings, vehicles, equipment.',
  financing: 'Money put into the school or repaid — loans, capital contributions.',
};

/**
 * Cash and equivalents. A flow BETWEEN these is a transfer, not a flow.
 *
 * Petty cash is included, unlike the sister deployment. It is cash the
 * school holds, and a float drawn from the bank is money moving pocket to
 * pocket rather than out of the school — counting it as an operating
 * outflow would overstate spending by the float every time it is topped up.
 */
const CASH_KEYS: SystemKey[] = ['BANK_MAIN', 'BANK_SECOND', 'CASH_ON_HAND', 'PETTY_CASH'];

/**
 * Where the chart splits current from non-current.
 *
 * There is no `isCurrent` flag on an account, so the grouping does the work:
 * LKC's seeded chart puts current assets under 1100 and what the school
 * keeps under 1500, current liabilities under 2100 and long-term under 2500.
 * Named here rather than left as bare numbers inside a switch, because a
 * school that renumbers its chart needs one place to look.
 */
const NON_CURRENT_ASSET_FROM = 1500;
const LONG_TERM_LIABILITY_FROM = 2500;

export interface FlowLine {
  /** The contra account the cash moved to or from. */
  accountCode: string;
  accountName: string;
  section: FlowSection;
  /** Signed: positive is cash in, negative is cash out. */
  amount: number;
  count: number;
}

export interface CashFlowStatement {
  from: string;
  to: string;
  openingCash: number;
  closingCash: number;
  /** closingCash − openingCash. What the statement must explain. */
  netMovement: number;
  sections: { section: FlowSection; lines: FlowLine[]; total: number }[];
  operating: number;
  investing: number;
  financing: number;
  /** The three sections added up. */
  classifiedTotal: number;
  /** netMovement − classifiedTotal. Nil when the statement is complete. */
  unexplained: number;
  reconciles: boolean;
  /** Cash moved between the school's own accounts — excluded, and reported so
   *  it is visibly excluded rather than silently missing. */
  internalTransfers: number;
}

/** Which accounts count as cash. Falls back to a name match for a school whose
 *  chart carries a bank account nobody system-keyed. */
export function cashAccountCodes(accounts: GlAccount[]): Set<string> {
  const out = new Set<string>();
  accounts.forEach((a) => {
    if (a.systemKey && CASH_KEYS.includes(a.systemKey)) out.add(a.code);
    else if (a.type === 'asset' && a.isPostable && /^(bank|cash)\b/i.test(a.name)) out.add(a.code);
  });
  return out;
}

/**
 * Classify a contra account.
 *
 * The distinction that carries the most weight is CURRENT vs NON-CURRENT assets:
 * a debtor is operating (fees not yet collected), a vehicle is investing. There
 * is no `isCurrent` flag on an account, so the parent code is used — the seeded
 * chart puts current assets under 1100 and non-current under 1500, and a school
 * that renumbers keeps the grouping because the parent is what the reports use
 * everywhere else.
 */
export function classifyContra(account: GlAccount | undefined): FlowSection {
  if (!account) return 'operating';
  switch (account.type) {
    case 'income':
    case 'expense':
      return 'operating';
    case 'equity':
      return 'financing';
    case 'asset':
      // Non-current assets are the things the school keeps.
      return account.parentCode && Number(account.parentCode) >= NON_CURRENT_ASSET_FROM
        ? 'investing' : 'operating';
    case 'liability':
      // Long-term liabilities fund the school; current ones are trading.
      return account.parentCode && Number(account.parentCode) >= LONG_TERM_LIABILITY_FROM
        ? 'financing' : 'operating';
    default:
      return 'operating';
  }
}

export function buildCashFlow(input: {
  accounts: GlAccount[];
  journals: GlJournal[];
  from: string;
  to: string;
}): CashFlowStatement {
  const { accounts, from, to } = input;
  const byCode = new Map(accounts.map((a) => [a.code, a]));
  const cash = cashAccountCodes(accounts);

  const live = input.journals.filter((j) => j.status !== 'reversed');

  // Opening and closing are computed the same way the Cash Book does it, so the
  // two screens cannot disagree about how much money there was.
  let openingCash = 0;
  let closingCash = 0;
  live.forEach((j) => (j.lines ?? []).forEach((l) => {
    if (!cash.has(l.accountCode)) return;
    const net = (Number(l.dr) || 0) - (Number(l.cr) || 0);
    // `from` guards OPENING but its absence must mean nil, not everything:
    // with no start date there is nothing before the period, so opening is zero.
    // `to` is the opposite — no end date means the period runs to the present,
    // so every entry counts towards closing.
    if (from && j.date < from) openingCash += net;
    if (!to || j.date <= to) closingCash += net;
  }));
  openingCash = round2(openingCash);
  closingCash = round2(closingCash);

  const agg = new Map<string, FlowLine>();
  let internalTransfers = 0;

  live
    .filter((j) => (!from || j.date >= from) && (!to || j.date <= to))
    .forEach((j) => {
      const lines = j.lines ?? [];
      const cashLines = lines.filter((l) => cash.has(l.accountCode));
      if (cashLines.length === 0) return;

      const contraLines = lines.filter((l) => !cash.has(l.accountCode));

      // Cash on both sides and nothing else: a transfer between the school's own
      // accounts. Real movement in the cash book, no flow to report.
      if (contraLines.length === 0) {
        internalTransfers = round2(
          internalTransfers + cashLines.reduce((n, l) => n + (Number(l.dr) || 0), 0),
        );
        return;
      }

      // A journal can carry several contra lines against one cash line (a payment
      // covering three expense codes). Each contra is attributed its own share,
      // with the SIGN taken from the cash side — the cash moved one way, whatever
      // the contra lines look like individually.
      const cashNet = round2(cashLines.reduce(
        (n, l) => n + (Number(l.dr) || 0) - (Number(l.cr) || 0), 0,
      ));
      if (cashNet === 0) return;

      const contraGross = contraLines.reduce(
        (n, l) => n + Math.abs((Number(l.dr) || 0) - (Number(l.cr) || 0)), 0,
      );
      if (contraGross === 0) return;

      contraLines.forEach((l) => {
        const share = Math.abs((Number(l.dr) || 0) - (Number(l.cr) || 0)) / contraGross;
        const amount = round2(cashNet * share);
        if (amount === 0) return;
        const account = byCode.get(l.accountCode);
        const key = l.accountCode;
        const cur = agg.get(key) ?? {
          accountCode: l.accountCode,
          accountName: account?.name ?? l.accountCode,
          section: classifyContra(account),
          amount: 0,
          count: 0,
        };
        cur.amount = round2(cur.amount + amount);
        cur.count += 1;
        agg.set(key, cur);
      });
    });

  const all = [...agg.values()].filter((l) => Math.abs(l.amount) >= 0.005);
  const sections = (['operating', 'investing', 'financing'] as FlowSection[]).map((section) => {
    const lines = all
      .filter((l) => l.section === section)
      .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    return { section, lines, total: round2(lines.reduce((n, l) => n + l.amount, 0)) };
  });

  const operating = sections[0].total;
  const investing = sections[1].total;
  const financing = sections[2].total;
  const classifiedTotal = round2(operating + investing + financing);
  const netMovement = round2(closingCash - openingCash);
  const unexplained = round2(netMovement - classifiedTotal);

  return {
    from,
    to,
    openingCash,
    closingCash,
    netMovement,
    sections,
    operating,
    investing,
    financing,
    classifiedTotal,
    unexplained,
    reconciles: Math.abs(unexplained) < 0.005,
    internalTransfers,
  };
}
